/* eslint-disable react/display-name */
import { updateTransactionAccountDetails } from '@atlassian/temp-proxies';
import {
  UpdateTransactionAccountPayload,
  UpdateTransactionAccountResponseToClient,
  FormattedResponseFromCCP,
} from '@atlassian/cssxp-types-billing';
import { sleep } from '@atlassian/cssxp-utils-generic';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../state/src/notifications-slice';
import {
  updateTransactionAccountFailedNotification,
  updateTransactionAccountSuccessNotification,
} from '../../notifications';

export const useUpdateTransactionAccount = ({
  onClose,
  onComplete,
  sendNotification = true,
  onError,
  notification,
  errorMsgList,
}: {
  onClose: () => void;
  /** Logic to run after a failed or successful attempt, such as refreshing data */
  onComplete: (updatedPayload?: UpdateTransactionAccountPayload) => void | Promise<void>;
  sendNotification?: boolean;
  onError?: (detailList?: (() => JSX.Element)[]) => void;
  notification?: { successMessage?: string; successHeading?: string; failureMessage?: string; failureHeading?: string };
  errorMsgList?: (errList?: FormattedResponseFromCCP[]) => string[] | void;
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [payload, setPayload] = useState<{
    transactionAccountId: string;
    updateTransactionAccountPayload: UpdateTransactionAccountPayload;
  }>();
  const dispatch = useDispatch();

  useEffect(() => {
    // Execute
    updateTransactionAccountEffect();

    // Clean up
    return () => {
      setPayload(undefined);
    };

    // Function definitions
    async function updateTransactionAccountEffect() {
      if (payload) {
        setIsProcessing(true);
        try {
          const result = await updateTransactionAccountDetails(
            payload.transactionAccountId,
            payload.updateTransactionAccountPayload
          );

          result.allSuccessful === true ? await handleSuccess() : await handleFail(result.errors);
        } catch (error) {
          await handleFail();
        }

        setIsProcessing(false);
      }
    }

    async function handleSuccess() {
      if (sendNotification) {
        const notificationOptions = updateTransactionAccountSuccessNotification({
          heading: notification?.successHeading,
          message: notification?.successMessage,
        });
        dispatch(addNotification(notificationOptions));
      }
      await refetchData();
      onClose();
    }

    async function handleFail(errorList?: UpdateTransactionAccountResponseToClient['errors']) {
      errorMsgList && errorMsgList(errorList);
      const elements = errorList?.map((error) => () => {
        let errorMessages: string[] = [];
        if (error.error?.payload?.validationErrors) {
          errorMessages = error.error?.payload?.validationErrors.map((message: any) => `${message?.validationMessage}`);
        } else {
          errorMessages.push(error.error?.message ?? '');
        }
        return (
          <>
            <p>
              <strong>
                {error.operationName}
                {error.failedEntity ? `: ${error.failedEntity}` : ''}
              </strong>
            </p>
            {errorMessages.map((message, index) => (
              <p key={index}>{message}</p>
            ))}
          </>
        );
      });
      onError && onError(elements);
      if (sendNotification) {
        // TODO :: Handle this notification with detailList post the support is available for it
        const renderErrorMessage = (message?: string, elements?: (() => JSX.Element)[]) => {
          return (
            <>
              {message ?? <div>{message}</div>}
              <>
                {elements?.map((DetailItem, index) => (
                  <DetailItem key={index} />
                ))}
              </>
            </>
          );
        };
        const notificationOptions = updateTransactionAccountFailedNotification({
          heading: notification?.failureHeading,
          message: renderErrorMessage(notification?.failureMessage, elements),
        });
        dispatch(addNotification(notificationOptions));
      }
    }

    async function refetchData() {
      // Delay to give hydra time to update with the latest data before refetching
      await sleep(2000);
      await onComplete(payload?.updateTransactionAccountPayload);
    }
  }, [payload, dispatch, onClose, onComplete]);

  return { isProcessing, updateTransactionAccount: setPayload };
};
